/**
 * Client-side "Grow along" invite link.
 *
 * Two share URL formats exist side-by-side:
 *
 *   1) `/g/<slug>`     — SHORT link. Requires an authenticated Drive-mode
 *                        user to create because the payload is stored
 *                        server-side (Mongo `invite_shortlinks` collection,
 *                        30-day TTL). Great for pasting into chat apps
 *                        and building small QR codes.
 *
 *   2) `/g#v{1|2}.<data>` — FRAGMENT link. Fully client-side; payload
 *                           lives entirely in `location.hash` so it is
 *                           NEVER sent to our server. Used as a fallback
 *                           in local mode or when the shortlink API is
 *                           unreachable. Legacy `v1.` (uncompressed
 *                           base64url) is still decoded on the receiving
 *                           end so previously-sent invites keep working.
 *
 * Fragment payload shape (both formats):
 *   { v: 2, s: "Sender name" | null, ts: iso-string,
 *     tc: total_active_plants,   // full library size (may exceed p.length)
 *     tr: truncated_by_cap,      // true iff we clipped at MAX_INVITE_PLANTS
 *     p: [ { n, s, c: {...} }, ... ] }
 *
 * PNG thumbnails are deliberately NOT included — the shareable image
 * already carries them and photos would blow past every chat app's link
 * length limit.
 */
import LZString from 'lz-string';
import axios from 'axios';
import { API_BASE_URL } from './apiBase';

// Which care fields are safe to include in the public invite. `toxicity_notes`
// is free-text so it's stripped to keep the payload small and predictable.
const _CARE_FIELDS = [
  'family', 'plant_type', 'watering', 'light', 'humidity',
  'temperature', 'toxicity', 'environment', 'growth_rate',
];

// Extended care fields for single-plant "full detail" invites (Option 1 in
// the share chooser). These are stripped from library-scale invites to
// keep them compact but shine on a dedicated single-plant page.
const _CARE_FIELDS_FULL = [
  ..._CARE_FIELDS,
  'bloom_season', 'bloom_duration', 'bloom_tips', 'blooms_indoors',
  'dormancy_notes', 'dormancy_months', 'toxicity_notes',
];

const MAX_INVITE_PLANTS = 12;

/** Convert standard base64 to URL-safe base64 (RFC 4648 §5). Kept only for
 *  decoding legacy `v1.` payloads generated before lz-string was added. */
function _b64UrlDecode(s) {
  const pad = s.length % 4 === 0 ? '' : '='.repeat(4 - (s.length % 4));
  const b64 = s.replace(/-/g, '+').replace(/_/g, '/') + pad;
  const binary = atob(b64);
  const bytes = new Uint8Array(binary.length);
  for (let i = 0; i < binary.length; i += 1) bytes[i] = binary.charCodeAt(i);
  return new TextDecoder().decode(bytes);
}

/**
 * Trim + shape the plant list into the compact invite payload plants
 * array. Shared by both fragment and shortlink code paths so both
 * carry identical data.
 *
 * The 12-plant cap only exists to keep FRAGMENT URLs under the various
 * chat-app / QR-code length limits. Short links store the payload
 * server-side, so they can hold the whole library — pass `full: true`
 * to skip the cap.
 *
 * `thumbnailMap` is an optional `Map<plant_id, dataUrl>` — when
 * supplied, each plant that has a thumb gets a compact `t` field so the
 * recipient's Grow Along page can render the image inline.
 */
function _shapePlantsForInvite(plants, { full = false, thumbnailMap = null } = {}) {
  const active = (plants || []).filter((p) => !p.archived && !p.incomplete);
  const source = full ? active : active.slice(0, MAX_INVITE_PLANTS);
  const trimmed = source.map((p) => {
    const care = p.care_details || {};
    const c = {};
    for (const k of _CARE_FIELDS) {
      if (care[k]) c[k] = care[k];
    }
    const record = {
      n: p.common_name || 'Unknown plant',
      s: p.scientific_name || null,
      c,
    };
    if (thumbnailMap && p.plant_id && thumbnailMap.has(p.plant_id)) {
      record.t = thumbnailMap.get(p.plant_id);
    }
    return record;
  });
  return { active, trimmed };
}

function _buildPayload({ trimmed, senderName, activeCount, truncatedByCap, note = null, viewState = null }) {
  return {
    v: 2,
    s: senderName ? String(senderName).slice(0, 60) : null,
    // Optional short greeting from the sender. Capped at 240 chars so
    // the compact fragment URL doesn't blow up and the GrowAlongPage
    // renders it in a single friendly card above the plant grid.
    n: note ? String(note).slice(0, 240) : null,
    // Deep-link view state — echoes the sender's library-side sort so
    // recipients see plants in the same order the sender was browsing.
    // Only whitelisted values are propagated to avoid leaking arbitrary
    // client state. Currently: sort primary + secondary (name_asc /
    // name_desc / one of the group-by fields).
    vs: viewState && typeof viewState === 'object'
      ? {
          sb: typeof viewState.sortBy === 'string' ? viewState.sortBy.slice(0, 32) : null,
          tb: typeof viewState.thenBy === 'string' ? viewState.thenBy.slice(0, 32) : null,
        }
      : null,
    ts: new Date().toISOString(),
    tc: activeCount,
    tr: !!truncatedByCap,
    p: trimmed,
  };
}

/**
 * Build a fragment-based invite URL. Fully synchronous, zero server touch.
 *
 * If `maxUrlLength` is provided, the plant list is progressively trimmed
 * (dropping trailing plants) until the resulting URL fits under the cap.
 * This is only relevant for the QR-code fallback path since QR codes have
 * a hard capacity ceiling (~2331 bytes at Version 40, Byte mode, EC-M).
 *
 * Returns `{ url, includedCount, activeCount, truncated, truncatedByCap }`
 * where:
 *   - `activeCount`      = full un-truncated library size (post archive/incomplete filter)
 *   - `includedCount`    = plants actually encoded into the URL
 *   - `truncatedByCap`   = library exceeded MAX_INVITE_PLANTS (12) before URL sizing
 *   - `truncated`        = includedCount < activeCount for ANY reason
 */
export function buildInviteUrl(plants, { senderName = null, note = null, viewState = null, origin, maxUrlLength = null } = {}) {
  const { active, trimmed } = _shapePlantsForInvite(plants);
  const activeCount = active.length;
  const truncatedByCap = activeCount > trimmed.length;

  const base = (origin || (typeof window !== 'undefined' && window.location.origin) || '').replace(/\/$/, '');

  const encodeWith = (plantList) => {
    const payload = _buildPayload({
      trimmed: plantList,
      senderName,
      note,
      viewState,
      activeCount,
      truncatedByCap: truncatedByCap || plantList.length < trimmed.length,
    });
    // lz-string's URI-safe variant emits characters already valid inside
    // `location.hash` — no further encoding needed.
    const encoded = LZString.compressToEncodedURIComponent(JSON.stringify(payload));
    return `${base}/g#v2.${encoded}`;
  };

  let url = encodeWith(trimmed);
  let includedCount = trimmed.length;
  if (maxUrlLength && url.length > maxUrlLength && includedCount > 0) {
    // Drop plants from the end one at a time until the URL fits.
    let candidate = trimmed.slice();
    while (candidate.length > 0 && url.length > maxUrlLength) {
      candidate = candidate.slice(0, -1);
      url = encodeWith(candidate);
    }
    includedCount = candidate.length;
  }

  return {
    url,
    includedCount,
    activeCount,
    // Keep `totalCount` alias for backwards-compat with older call sites
    // that read the QR modal's truncation message.
    totalCount: activeCount,
    truncatedByCap,
    truncated: includedCount < activeCount,
  };
}

/**
 * Build a SHORT invite URL (`/g/<slug>`) by uploading the payload to the
 * backend. Requires a Drive-mode session token; falls back to the
 * fragment URL in local mode or on network failure so the sharing UI
 * never breaks.
 *
 * By default the WHOLE library is uploaded — short links store the
 * payload server-side so there's no URL length constraint. Pass
 * `full: false` to preview-cap at 12 plants (mostly useful for tests).
 *
 * `thumbnailMap` is an optional `Map<plant_id, dataUrl>` from
 * `batchGetInviteThumbnails`. When supplied, each plant with a
 * thumbnail gets a `t` field in the payload for rich preview. If the
 * resulting payload exceeds `MAX_PAYLOAD_BYTES` we progressively strip
 * thumbnails from the tail plants (keeping the FIRST N with photos)
 * until the payload fits — the recipient always sees plants without
 * thumbnails; we just may not have photos for every tile.
 *
 * Returns the same shape as `buildInviteUrl` plus `{ slug, expires_at,
 * short: true|false, thumbnailsIncluded: N }`.
 */
const MAX_PAYLOAD_BYTES = 3_500_000; // stays comfortably under backend 4 MB cap
export async function createShortInviteUrl(
  plants,
  { senderName = null, note = null, viewState = null, origin, sessionToken = null, full = true, thumbnailMap = null } = {}
) {
  const { active, trimmed } = _shapePlantsForInvite(plants, { full, thumbnailMap });
  const activeCount = active.length;
  const truncatedByCap = activeCount > trimmed.length;
  let payloadPlants = trimmed;

  // Iteratively strip trailing thumbnails until payload fits the cap.
  // Never removes plants themselves — recipients always see the whole
  // roster, just some cards may fall back to a leaf placeholder.
  let thumbnailsIncluded = payloadPlants.filter((p) => !!p.t).length;
  let payload = _buildPayload({
    trimmed: payloadPlants,
    senderName,
    note,
    viewState,
    activeCount,
    truncatedByCap,
  });
  let currentSize = JSON.stringify(payload).length;
  if (currentSize > MAX_PAYLOAD_BYTES) {
    // Walk from the tail, dropping `t` fields until we fit.
    const stripped = payloadPlants.map((p) => ({ ...p }));
    for (let i = stripped.length - 1; i >= 0 && currentSize > MAX_PAYLOAD_BYTES; i -= 1) {
      if (stripped[i].t) {
        delete stripped[i].t;
        payload = _buildPayload({
          trimmed: stripped,
          senderName,
          note,
          viewState,
          activeCount,
          truncatedByCap,
        });
        currentSize = JSON.stringify(payload).length;
      }
    }
    payloadPlants = stripped;
    thumbnailsIncluded = payloadPlants.filter((p) => !!p.t).length;
  }

  const base = (origin || (typeof window !== 'undefined' && window.location.origin) || '').replace(/\/$/, '');
  const includedCount = payloadPlants.length;
  const commonMeta = {
    includedCount,
    activeCount,
    totalCount: activeCount,
    truncatedByCap,
    truncated: includedCount < activeCount,
    thumbnailsIncluded,
  };

  // Anonymous callers are now supported by the backend (iter 103) — try
  // the shortlink endpoint whether or not we have a session token; the
  // `Authorization` header is only sent when a token exists. If the
  // backend rejects (401/403/429/500), fall back to the fragment URL
  // in the catch block below so sharing always works.
  try {
    const headers = sessionToken ? { Authorization: `Bearer ${sessionToken}` } : {};
    const resp = await axios.post(
      `${API_BASE_URL}/api/invite/shortlink`,
      { payload, total_count: activeCount },
      { headers, timeout: 30000 }
    );
    const slug = resp.data?.slug;
    if (!slug) throw new Error('Server did not return a slug');
    return {
      url: `${base}/g/${slug}`,
      slug,
      expires_at: resp.data?.expires_at || null,
      short: true,
      ...commonMeta,
    };
  } catch (err) {
    // Any failure — silently fall back to the fragment URL so the user
    // can still share. The caller can tell the URL kind via `short`.
    console.debug('Shortlink create failed, falling back to fragment URL:', err?.message);
    return {
      ...buildInviteUrl(plants, { senderName, origin }),
      short: false,
      thumbnailsIncluded: 0,
    };
  }
}

/**
 * Build a SHORT single-plant invite URL (`/g/plant/<slug>`) with the FULL
 * care details for one plant. Used by "Share this plant" in the
 * PlantDetailModal — the receiver lands on a single-card page with all
 * bloom / dormancy / toxicity notes visible (see GrowAlongPage `kind`
 * branch).
 *
 * In local mode / no session, falls back to a `/g/plant#v2plant.<data>`
 * fragment URL so sharing still works offline. Fragment payloads strip
 * the thumbnail to stay under URL length limits — the plant card just
 * renders the leaf placeholder in that case.
 */
export async function createPlantShortInviteUrl(
  plant,
  { senderName = null, note = null, origin, sessionToken = null, thumbDataUrl = null } = {},
) {
  const base = (origin || (typeof window !== 'undefined' && window.location.origin) || '').replace(/\/$/, '');
  const care = plant?.care_details || {};
  const shapedCare = {};
  for (const k of _CARE_FIELDS_FULL) {
    if (care[k]) shapedCare[k] = care[k];
  }
  const record = {
    n: plant?.common_name || 'Unknown plant',
    s: plant?.scientific_name || null,
    c: shapedCare,
  };
  if (thumbDataUrl) record.t = thumbDataUrl;

  const payload = {
    v: 2,
    kind: 'plant',
    s: senderName ? String(senderName).slice(0, 60) : null,
    n: note ? String(note).slice(0, 240) : null,
    ts: new Date().toISOString(),
    tc: 1,
    tr: false,
    p: [record],
  };

  // Anonymous callers supported (iter 103) — always attempt the
  // shortlink endpoint; fall back to fragment on any failure.
  try {
    const headers = sessionToken ? { Authorization: `Bearer ${sessionToken}` } : {};
    const resp = await axios.post(
      `${API_BASE_URL}/api/invite/shortlink`,
      { payload, total_count: 1 },
      { headers, timeout: 30000 },
    );
    const slug = resp.data?.slug;
    if (!slug) throw new Error('Server did not return a slug');
    return {
      url: `${base}/g/plant/${slug}`,
      slug,
      expires_at: resp.data?.expires_at || null,
      short: true,
      kind: 'plant',
      includedCount: 1,
      activeCount: 1,
      totalCount: 1,
      truncated: false,
    };
  } catch (err) {
    console.debug('Plant shortlink create failed, falling back to fragment URL:', err?.message);
    // Fragment fallback: compress the payload sans thumbnail into the URL hash.
    const compactRecord = { n: record.n, s: record.s, c: record.c };
    const compactPayload = { ...payload, p: [compactRecord] };
    const LZString = (await import('lz-string')).default;
    const encoded = LZString.compressToEncodedURIComponent(JSON.stringify(compactPayload));
    return {
      url: `${base}/g/plant#v2.${encoded}`,
      short: false,
      kind: 'plant',
      includedCount: 1,
      activeCount: 1,
      totalCount: 1,
      truncated: false,
    };
  }
}

/**
 * Fetch and decode a shortlink slug from the backend. Returns the same
 * shape `decodeInviteFragment` returns, or null on 404 / network error.
 */
export async function resolveShortInviteSlug(slug) {
  if (!slug || typeof slug !== 'string') return null;
  try {
    const resp = await axios.get(`${API_BASE_URL}/api/invite/shortlink/${encodeURIComponent(slug)}`, { timeout: 10000 });
    const payload = resp.data?.payload;
    if (payload && (payload.v === 1 || payload.v === 2) && Array.isArray(payload.p)) {
      return payload;
    }
    return null;
  } catch {
    return null;
  }
}

/**
 * Decode a `/g#v{1|2}.<data>` fragment into the invite payload, or `null`
 * if the fragment is malformed / unsupported version.
 */
export function decodeInviteFragment(hash) {
  if (!hash || typeof hash !== 'string') return null;
  const clean = hash.startsWith('#') ? hash.slice(1) : hash;
  const dotIdx = clean.indexOf('.');
  if (dotIdx < 0) return null;
  const version = clean.slice(0, dotIdx);
  const data = clean.slice(dotIdx + 1);
  if (!data) return null;
  try {
    let json;
    if (version === 'v2') {
      json = LZString.decompressFromEncodedURIComponent(data);
      if (!json) return null;
    } else if (version === 'v1') {
      json = _b64UrlDecode(data);
    } else {
      return null;
    }
    const parsed = JSON.parse(json);
    if (parsed && (parsed.v === 1 || parsed.v === 2) && Array.isArray(parsed.p)) {
      return parsed;
    }
    return null;
  } catch {
    return null;
  }
}
